import moment from 'moment';
import messages from './messages';
import { parsePortalAccessInfo } from '../../../hooks/data-fetching/useHouseholdMembersQuery.helpers';

export const PORTAL_ACTIONS = {
  SEND_INVITE: 'sendInvite',
  RESET_PASSWORD: 'resetPassword',
  TOGGLE_BLOCK: 'toggleBlock',
  RESET_ACCOUNT: 'resetAccount',
  RESET_LOGIN_ATTEMPTS: 'resetLoginAttempts',
};

// An applicant has not moved in, so password resets, blocking and lockouts do
// not apply yet. Reset Account does: it is the way out when the account was
// claimed under an address the household has since changed.
const APPLICANT_ACTIONS = [
  PORTAL_ACTIONS.SEND_INVITE,
  PORTAL_ACTIONS.RESET_ACCOUNT,
];

// canSendPortalInvite also requires canBlockAccount, so a blocked account
// fails that for a reason that has nothing to do with its email address --
// hence the mention of blocking in the invite tooltip.
const UNAVAILABLE_TOOLTIPS = {
  [PORTAL_ACTIONS.SEND_INVITE]: messages.portalSendInviteUnavailable,
  [PORTAL_ACTIONS.RESET_PASSWORD]: messages.portalResetPasswordUnavailable,
  [PORTAL_ACTIONS.TOGGLE_BLOCK]: messages.portalToggleBlockUnavailable,
  [PORTAL_ACTIONS.RESET_ACCOUNT]: messages.portalResetAccountUnavailable,
  [PORTAL_ACTIONS.RESET_LOGIN_ATTEMPTS]:
    messages.portalResetLoginAttemptsUnavailable,
};

const isBlockToggleAvailable = (portalStatus) =>
  Boolean(portalStatus.canBlockAccount || portalStatus.canUnblockAccount);

// The endpoint is one toggle, so this is one option whose label follows the
// account's current state rather than two mutually-exclusive entries.
const blockToggleLabel = (portalStatus) =>
  portalStatus.canUnblockAccount
    ? messages.portalUnblockAccountAction
    : messages.portalBlockAccountAction;

const buildOption = (formatMessage) => (value, label, isAvailable) => ({
  value,
  text: formatMessage(label),
  disabled: !isAvailable,
  // Unavailable actions stay listed rather than disappearing, so the reason
  // has to be reachable — the legacy panel simply hid them.
  ...(isAvailable
    ? {}
    : { tooltip: formatMessage(UNAVAILABLE_TOOLTIPS[value]) }),
});

export const buildPortalActionOptions = (
  portalStatus = {},
  formatMessage,
  // sendInviteBlockedByPrimary: AffordaPortal-only rule the backend cannot know, so it overrides
  // canSendPortalInvite here. isApplicantSubject narrows the list to APPLICANT_ACTIONS.
  { sendInviteBlockedByPrimary = false, isApplicantSubject = false } = {},
) => {
  const buildOptionWith = buildOption(formatMessage);

  const options = [
    buildOptionWith(
      PORTAL_ACTIONS.SEND_INVITE,
      messages.portalSendInviteAction,
      Boolean(portalStatus.canSendPortalInvite),
    ),
    buildOptionWith(
      PORTAL_ACTIONS.RESET_PASSWORD,
      messages.portalResetPasswordAction,
      // canResetPassword is derived from claimed + active only; the reset
      // endpoint is addressed by email, so it has to be both present and
      // usable -- a cleared address would send one addressed to undefined,
      // and an address the backend already rejects would send one nobody
      // receives.
      Boolean(
        portalStatus.canResetPassword &&
          portalStatus.emailAddress &&
          portalStatus.isEmailValid !== false,
      ),
    ),
    buildOptionWith(
      PORTAL_ACTIONS.TOGGLE_BLOCK,
      blockToggleLabel(portalStatus),
      isBlockToggleAvailable(portalStatus),
    ),
    buildOptionWith(
      PORTAL_ACTIONS.RESET_ACCOUNT,
      messages.portalResetAccountAction,
      Boolean(portalStatus.canResetAccount),
    ),
    buildOptionWith(
      PORTAL_ACTIONS.RESET_LOGIN_ATTEMPTS,
      messages.portalResetLoginAttemptsAction,
      Boolean(portalStatus.canResetLoginAttempts),
    ),
  ];

  const scopedOptions = isApplicantSubject
    ? options.filter((option) => APPLICANT_ACTIONS.includes(option.value))
    : options;

  return scopedOptions.map((option) =>
    sendInviteBlockedByPrimary && option.value === PORTAL_ACTIONS.SEND_INVITE
      ? {
          ...option,
          disabled: true,
          tooltip: formatMessage(
            messages.portalSendInviteNonPrimaryUnavailable,
          ),
        }
      : option,
  );
};

// Mirrors PortalUsers.js#checkAuthorization: a Prior member loses Portal
// access the moment their end date is reached, enforced at login. Surfaced
// here so it is visible before the resident reports a failed login.
// endDate arrives already formatted for display by
// useHouseholdMembersQuery.helpers.js (formatCalendarDateUtc -> MM/DD/YYYY),
// so parse it explicitly rather than letting moment guess.
const END_DATE_FORMATS = ['MM/DD/YYYY', moment.ISO_8601];

export const isPortalBlockedByEndDate = (memberStatus, endDate) => {
  if (memberStatus !== 'Prior' || !endDate) return false;
  // UTC, not local: the backend compares against a UTC-midnight endDate, so a
  // local parse would disagree with it by the property's offset -- west of
  // UTC the resident is locked out for hours before this would say so.
  const parsed = moment.utc(endDate, END_DATE_FORMATS);
  return parsed.isValid() && moment().isSameOrAfter(parsed);
};

export const buildPortalStatusBanners = ({
  portalStatus = {},
  memberStatus,
  endDate,
  hasPortalStatus = false,
  portalStatusFailed = false,
  isApplicantSubject = false,
  formatMessage,
} = {}) => {
  const banners = [];

  if (isPortalBlockedByEndDate(memberStatus, endDate)) {
    banners.push({
      status: 'warning',
      message: formatMessage(messages.portalRemovedFromHousehold),
    });
  }
  // portalStatus is only fetched for Current members and the applicant subject; for anyone
  // else it is {} and every check below would read that as a real answer.
  if (memberStatus !== 'Current' && !isApplicantSubject) return banners;

  if (portalStatusFailed) {
    banners.push({
      status: 'error',
      message: formatMessage(messages.portalStatusUnavailable),
    });
    return banners;
  }
  // Same empty-object trap while the query is still in flight: it would read
  // as "no email, no account" and show a banner that is simply not known yet.
  if (!hasPortalStatus) return banners;

  const isClaimed = Boolean(portalStatus.portalUser?.isClaimed);

  if (portalStatus.canUnblockAccount) {
    banners.push({
      status: 'warning',
      message: formatMessage(messages.portalAccountBlockedBanner),
    });
  }
  if (portalStatus.canResetLoginAttempts) {
    banners.push({
      status: 'warning',
      message: formatMessage(messages.portalLockedOutBanner),
    });
  }
  if (!portalStatus.emailAddress) {
    banners.push({
      status: 'info',
      message: formatMessage(
        isClaimed
          ? messages.portalNeedEmailForReset
          : messages.portalNeedEmailForInvite,
      ),
    });
  }
  if (portalStatus.emailAddress && portalStatus.isEmailValid === false) {
    banners.push({
      status: 'error',
      message: portalStatus.emailValidationReason
        ? formatMessage(messages.portalInvalidEmailWithReason, {
            reason: portalStatus.emailValidationReason,
          })
        : formatMessage(messages.portalInvalidEmail),
    });
  }
  if (portalStatus.isEmailClaimed && !portalStatus.portalUser) {
    banners.push({
      status: 'error',
      message: formatMessage(messages.portalEmailAlreadyInUse),
    });
  }

  return banners;
};

// The confirm button reuses the action's own label, so a confirmation never
// names the action differently from the option that opened it.
const CONFIRMATION_COPY = {
  [PORTAL_ACTIONS.RESET_ACCOUNT]: {
    title: messages.portalResetAccountConfirmTitle,
    body: messages.portalResetAccountConfirmBody,
    confirmLabel: messages.portalResetAccountAction,
  },
  block: {
    title: messages.portalBlockConfirmTitle,
    body: messages.portalBlockConfirmBody,
    confirmLabel: messages.portalBlockAccountAction,
  },
  unblock: {
    title: messages.portalUnblockConfirmTitle,
    body: messages.portalUnblockConfirmBody,
    confirmLabel: messages.portalUnblockAccountAction,
  },
};

// Only the actions that remove access or force a re-claim confirm; the other
// three send an email or clear a counter and are reversible.
export const getConfirmationCopy = (
  action,
  portalStatus = {},
  formatMessage,
) => {
  let copy = null;

  if (action === PORTAL_ACTIONS.RESET_ACCOUNT) {
    copy = CONFIRMATION_COPY[PORTAL_ACTIONS.RESET_ACCOUNT];
  }
  if (action === PORTAL_ACTIONS.TOGGLE_BLOCK) {
    copy = portalStatus.canUnblockAccount
      ? CONFIRMATION_COPY.unblock
      : CONFIRMATION_COPY.block;
  }
  if (!copy) return null;

  return {
    title: formatMessage(copy.title),
    body: formatMessage(copy.body),
    confirmLabel: formatMessage(copy.confirmLabel),
  };
};

// AffordaPortal: non-primary adults wait until the Head of Household has claimed and confirmed.
// A pending answer blocks; isFetching, not isLoading, because a reopen refetches over warm cache.
export const isSendInviteBlockedByPrimary = ({
  isApplicantSubject = false,
  isAffordaPortal = false,
  isPrimary = false,
  isPrimaryStatusPending = false,
  primaryStatus,
} = {}) => {
  if (!isApplicantSubject || !isAffordaPortal || isPrimary) return false;

  return (
    isPrimaryStatusPending ||
    !primaryStatus?.primaryIsClaimed ||
    !primaryStatus?.primaryHouseholdComplete
  );
};

// The applicant record carries no lastSystemAccess, so these come from the status endpoint.
export const buildApplicantPortalAccess = (portalStatus) =>
  parsePortalAccessInfo(portalStatus?.portalUser ?? {}, portalStatus ?? {});
